import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import notificationCenterService from '../../services/notificationCenterService';
import PriorityAlertSummary from './components/PriorityAlertSummary';
import NotificationFeed from './components/NotificationFeed';
import NotificationFilters from './components/NotificationFilters';

const CentralizedNotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [prioritySummary, setPrioritySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    severity: 'all',
    status: 'all',
    department: 'all',
    sortBy: 'date',
    search: ''
  });
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [realtimeChannels, setRealtimeChannels] = useState(null);
  const [newNotificationCount, setNewNotificationCount] = useState(0);

  useEffect(() => {
    loadData();

    // Subscribe to real-time notifications
    const channels = notificationCenterService?.subscribeToAllNotifications((newNotification, eventType) => {
      if (eventType === 'INSERT') {
        // Add new notification to the top of the list
        setNotifications(prev => [newNotification, ...prev]);
        
        // Increment new notification counter
        setNewNotificationCount(prev => prev + 1);
        
        // Refresh summary to reflect new counts
        loadPrioritySummary();
        
        // Show browser notification for critical alerts
        if (newNotification?.severity === 'critical' && 'Notification' in window) {
          if (Notification.permission === 'granted') {
            new Notification(newNotification.title, {
              body: newNotification.message,
              icon: '/favicon.ico',
              badge: '/favicon.ico'
            });
          } else if (Notification.permission !== 'denied') {
            Notification.requestPermission()?.then(permission => {
              if (permission === 'granted') {
                new Notification(newNotification.title, {
                  body: newNotification.message,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico'
                });
              }
            });
          }
        }
      } else if (eventType === 'UPDATE') {
        // Update existing notification
        setNotifications(prev => 
          prev?.map(n => n?.id === newNotification?.id ? { ...n, ...newNotification } : n)
        );
        loadPrioritySummary();
      }
    });

    setRealtimeChannels(channels);

    return () => {
      notificationCenterService?.unsubscribeAll(channels);
    };
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [filters]);

  const loadData = async () => {
    await Promise?.all([loadNotifications(), loadPrioritySummary()]);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationCenterService?.getAllNotifications(filters);
      setNotifications(data);
      setError('');
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadPrioritySummary = async () => {
    try {
      const summary = await notificationCenterService?.getPriorityAlertSummary();
      setPrioritySummary(summary);
    } catch (err) {
      console.error('Error loading priority summary:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedNotifications([]);
    // Reset new notification counter when filters change
    setNewNotificationCount(0);
  };

  const handleNotificationClick = (notification) => {
    // Navigate to relevant page based on notification type
    switch (notification?.category) {
      case 'compliance': navigate('/compliance-overview');
        break;
      case 'payroll': navigate('/payroll-compliance');
        break;
      case 'deadline':
        if (notification?.type === 'nasfund_deadline' || notification?.type === 'swt_deadline') {
          navigate('/payroll-compliance');
        }
        break;
      default:
        break;
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      if (prev?.includes(notificationId)) {
        return prev?.filter(id => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedNotifications?.length === notifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications?.map(n => n?.id));
    }
  };

  const handleBulkMarkAsRead = async () => {
    try {
      await notificationCenterService?.markMultipleAsRead(selectedNotifications);
      await loadNotifications();
      setSelectedNotifications([]);
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleBulkDismiss = async () => {
    try {
      await notificationCenterService?.dismissMultiple(selectedNotifications);
      await loadNotifications();
      setSelectedNotifications([]);
    } catch (err) {
      console.error('Error dismissing notifications:', err);
    }
  };

  const handleExport = async () => {
    try {
      await notificationCenterService?.exportNotifications(notifications, 'csv');
    } catch (err) {
      console.error('Error exporting notifications:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 relative flex-shrink-0">
              <Icon name="Bell" size={24} color="var(--color-primary)" />
              {newNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center w-6 h-6 bg-error text-error-foreground text-xs font-bold rounded-full animate-pulse">
                  {newNotificationCount > 9 ? '9+' : newNotificationCount}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground break-words">
                Centralized Notification Center
              </h1>
              <p className="text-sm text-muted-foreground mt-2 break-words">
                Real-time monitoring of compliance violations, deadline warnings, payroll errors, and system alerts
              </p>
            </div>
          </div>
          
          {/* Real-time Status Indicator */}
          <div className="flex items-center gap-2 mt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 text-success rounded-full">
              <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
              <span className="text-xs font-semibold">Live Updates Active</span>
            </div>
            {newNotificationCount > 0 && (
              <button
                onClick={() => setNewNotificationCount(0)}
                className="text-xs text-primary hover:underline"
              >
                Clear {newNotificationCount} new alert{newNotificationCount > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>

        {/* Priority Alert Summary */}
        {prioritySummary && (
          <div className="mb-8">
            <PriorityAlertSummary summary={prioritySummary} onCategoryClick={(category) => handleFilterChange('category', category)} />
          </div>
        )}

        {/* Filters and Actions */}
        <div className="mb-6">
          <NotificationFilters 
            filters={filters} 
            onFilterChange={handleFilterChange}
            selectedCount={selectedNotifications?.length}
            totalCount={notifications?.length}
            onSelectAll={handleSelectAll}
            onBulkMarkAsRead={handleBulkMarkAsRead}
            onBulkDismiss={handleBulkDismiss}
            onExport={handleExport}
          />
        </div>

        {/* Notification Feed */}
        <div className="bg-card border border-border rounded-lg shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mb-4" />
              <p className="text-error text-center">{error}</p>
              <button
                onClick={loadData}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
              >
                Retry
              </button>
            </div>
          ) : (
            <NotificationFeed 
              notifications={notifications}
              selectedNotifications={selectedNotifications}
              onNotificationClick={handleNotificationClick}
              onSelectNotification={handleSelectNotification}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default CentralizedNotificationCenter;
